import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
    TextInput,
} from 'grommet';
import {
    API,
    resolveSortField,
    RowsPerPageSelect,
    usePaginatedSort,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { useDebouncedCallback } from 'use-debounce';

export const RecentApprovals = () => {
    const [approvals, setApprovals] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'approvalDate', direction: 'desc' },
    });

    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, 400);

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadApprovals({
                page,
                pageSize,
                sort,
                searchTerm: appliedSearchTerm,
            });

            if (!isActive) return;
            setApprovals(response?.data ?? []);
            if (response?.meta?.pagination) {
                applyPagination(response.meta);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, sort, appliedSearchTerm, applyPagination]);

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (approval) => {
                return (
                    <Anchor
                        href={`/contacts/${approval.contactId}`}
                        label={`${approval.attributes.firstName} ${approval.attributes.lastName}`}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Onboarding Stage',
            property: 'attributes.onboardingStage',
            render: (approval) => {
                if (approval.attributes.onboardingCompleted) return 'completed';
                if (!approval.attributes.user?.data) {
                    return 'account not created';
                }
                return approval.attributes.onboardingStage.replace('_', ' ');
            },
            sortable: true,
        },
        {
            header: 'Approval Date',
            property: 'approvalDate',
            render: (approval) => {
                return new Date(approval.approvalDate).toLocaleDateString();
            },
            sortable: true,
        },
        {
            header: 'Approved By',
            property: 'approvedBy',
            sortable: true,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Heading level="2">Recent Approvals</Heading>
                    <Box width="medium">
                        <TextInput
                            a11yTitle="Search approvals"
                            placeholder="Search by student name"
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value);
                                applySearchTerm(event.target.value);
                            }}
                        />
                    </Box>
                </Box>

                <Box>
                    {approvals.length === 0 ? (
                        <Box pad="medium" align="center">
                            <Text>No approvals found</Text>
                        </Box>
                    ) : (
                        <DataTable
                            primaryKey="id"
                            sort={{ ...sort, external: true }}
                            onSort={handleSort}
                            data={approvals}
                            columns={dataColumns}
                        />
                    )}
                </Box>
                <Box
                    direction="row-responsive"
                    align="center"
                    justify="between"
                    gap="small"
                    pad="small">
                    <RowsPerPageSelect
                        options={stepOptions}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    />
                    <Pagination
                        numberItems={totalItems}
                        page={page}
                        onChange={handlePageChange}
                        step={pageSize}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

// Rows are approval *events*, so every sortable column maps onto the event
// or one of its relations. Strapi joins for relation sorts; both `contact`
// and `source_user` are many-to-one, so no duplicate rows appear.
const SORT_FIELD_MAP = {
    'attributes.lastName': 'contact.lastName',
    'attributes.onboardingStage': 'contact.onboardingStage',
    approvalDate: 'createdAt',
    approvedBy: 'source_user.lastName',
};

const DAYS_SHOWN = 30;

const loadApprovals = async ({ page, pageSize, sort, searchTerm } = {}) => {
    const sortBy = resolveSortField(sort, SORT_FIELD_MAP, 'createdAt');
    const sortDir = sort?.direction ?? 'desc';
    const term = searchTerm?.trim();
    const today = new Date();
    const windowStart = new Date(
        new Date().setDate(today.getDate() - DAYS_SHOWN),
    );

    const query = QueryString.stringify({
        filters: {
            type: 'application_approved',
            createdAt: {
                $gt: windowStart,
            },
            // Drop events whose contact was deleted in the query rather
            // than after the fetch, so the server's total matches the rows
            // actually rendered on the page.
            contact: {
                id: { $notNull: true },
            },
            ...(term
                ? {
                      $or: [
                          { contact: { firstName: { $containsi: term } } },
                          { contact: { lastName: { $containsi: term } } },
                      ],
                  }
                : {}),
        },
        populate: {
            contact: {
                populate: ['user'],
            },
            source_user: true,
        },
        pagination: page && pageSize ? { page, pageSize } : undefined,
        // `id:desc` is the tiebreaker: approvals made in the same second
        // would otherwise order non-deterministically under LIMIT/OFFSET
        // and could repeat or vanish across page boundaries.
        sort: [`${sortBy}:${sortDir}`, 'id:desc'],
    });

    try {
        const response = await API.get(`/api/events?${query}`);
        return {
            data: response.data.data.map(toApprovalRow),
            meta: response.data.meta,
        };
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page } } };
};

// One row per approval event. The row is keyed by the event id, not the
// contact id: a contact approved more than once produces several rows, and
// duplicate keys break DataTable's primaryKey.
const toApprovalRow = (event) => {
    const contact = event.attributes.contact.data;
    const approver = event.attributes.source_user?.data?.attributes;

    return {
        id: event.id,
        contactId: contact.id,
        attributes: contact.attributes,
        approvalDate: event.attributes.createdAt,
        approvedBy: approver
            ? `${approver.firstName} ${approver.lastName}`
            : 'Automated',
    };
};
